import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import { ExportData } from "src/app/shared/data/exportData";
import {
  FilterInterface,
  VoucherFilter,
} from "src/app/shared/data/filter-data";
import { Authorities } from "src/app/shared/enum/authorities";
import {
  VoucherStatus,
  VoucherStatusMapping,
} from "src/app/shared/enum/VoucherStatus";
import { SuccessDto } from "src/app/shared/models/success-dto";
import { IColumn } from "src/app/shared/models/table/column.interface";
import { RoleEnum } from "src/app/shared/models/user/role.interface";
import { VoucherDto } from "src/app/shared/models/voucher/voucherDto";
import { VoucherListFilterDto } from "src/app/shared/models/voucher/voucherListFilterDto";
import { VoucherWithResultDto } from "src/app/shared/models/voucher/voucherWithResultDto";
import { BadgeService } from "src/app/shared/services/badge.service";
import { JwtService } from "src/app/shared/services/jwt.service";
import { invokeApiDistroyed } from "src/app/store/apiState.interface";
import { AppStateInterface } from "src/app/store/appState.interface";
import { resultSindingMail } from "src/app/store/mailing/mailing.actions";
import { sendMailSelector } from "src/app/store/mailing/mailing.selectors";
import {
  invokeVouchersForLocationPartner,
  resultVouchersForLocationPartner,
} from "src/app/store/voucher/voucher.action";
import { initialState } from "src/app/store/voucher/voucher.reducers";
import {
  resultLoadingVoucherSelector,
  resultVouchersForLocationPartnerSelector,
} from "src/app/store/voucher/voucher.selectors";
import { UtilFunctions } from "src/app/utils/utilFunctions";

@Component({
  selector: "app-vouchers",
  templateUrl: "./vouchers.component.html",
})
export class VouchersComponent extends BaseComponent implements OnInit {
  @Input() locationPartnerEmail: string;
  @Input() mode: string = "card";

  @Output() closeTheModal: EventEmitter<boolean> = new EventEmitter();

  formFilter: FormGroup;

  voucherCSVData: ExportData[] = [
    {
      data: "",
      id: "vouchers.location_partner.table",
      name: "vouchers",
      dates: null,
      type: null,
    },
  ];

  columns: IColumn[] = [
    {
      key: "code",
      label: "admin.locations.voucher.code",
      sorter: false,
    },
    {
      key: "creationDate",
      label: "admin.locations.voucher.creationDate",
      sorter: false,
    },
    {
      key: "expirationDate",
      label: "admin.locations.voucher.expirationDate",
      sorter: false,
    },
    {
      key: "maxUsers",
      label: "admin.locations.voucher.maxUsers",
      sorter: false,
    },
    {
      key: "consumerNumber",
      label: "admin.locations.voucher.consumerNumber",
      sorter: false,
    },
    {
      key: "status",
      label: "admin.locations.voucher.status",
      sorter: false,
    },
  ];

  loading$: Observable<boolean | null>;

  voucherList: VoucherDto[];
  resultVouchersForLocationPartner$: Observable<VoucherWithResultDto | null>;

  forbidden: boolean = false;

  count: number = 0;
  activeCount: number = 0;

  VoucherFilter: FilterInterface = VoucherFilter;
  voucherFilter: VoucherListFilterDto = {
    ...initialState.voucherFilter,
  };

  resultSendStatics$: Observable<SuccessDto | null>;
  pageSize: number = Constants.defaultTableSize;
  pageNumber: number = 1;
  constructor(
    public badgeService: BadgeService,
    private store: Store<AppStateInterface>,
    private fb: FormBuilder,
    private jwtService: JwtService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {
    super(store);
    this.resultVouchersForLocationPartner$ = this.store
      .pipe(select(resultVouchersForLocationPartnerSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.loading$ = this.store
      .pipe(select(resultLoadingVoucherSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.resultSendStatics$ = this.store
      .pipe(select(sendMailSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.formFilter = this.fb.group({
      code: [null],
      date: [[null, null]],
    });

    const authorities = this.jwtService.getPermissions(jwtService.getToken());
    if (authorities) {
      this.forbidden = !authorities.includes(
        Authorities.VOUCHER_GETALL.toString()
      );
    }
  }

  ngOnInit(): void {
    this.store.dispatch(resultVouchersForLocationPartner({ vouchers: null }));
    this.resultSendStatics$.subscribe((result: SuccessDto | null) => {
      if (result != null) {
        this.store.dispatch(resultSindingMail({ successMessage: null }));
        this.toastr.success(
          this.translate.instant("response.success." + result.message),
          this.translate.instant("response.successTitle")
        );
      }
    });
    this.resultVouchersForLocationPartner$.subscribe((result) => {
      if (result) {
        this.voucherList = result.vouchers;
        this.voucherCSVData[0].data = UtilFunctions.convertToCSV(
          this.translateVoucherData(result.vouchers)
        );

        this.count = result.count;
        this.activeCount = result.vouchers.filter(
          ({ consumerNumber, maxUsers, expirationDate }) =>
            consumerNumber < maxUsers && new Date() < new Date(expirationDate)
        ).length;
      } else {
        let locationPartnerEmail: any = this.locationPartnerEmail;
        if (
          this.jwtService.getRoleName(this.jwtService.getToken()) ===
          RoleEnum.ROLE_LOCATION
        ) {
          locationPartnerEmail = this.jwtService.getEmail();
        }
        this.store.dispatch(
          invokeVouchersForLocationPartner({
            filter: {
              ...this.voucherFilter,
              locationPartnerEmail,
            },
          })
        );
      }
    });
    this.VoucherFilter.name.map(
      (e) =>
        (e.data = Object.keys(VoucherStatus)
          .filter((value) => isNaN(Number(value)) === true)
          .map(
            (key: string | any) =>
              VoucherStatusMapping[key as keyof typeof VoucherStatus]
          ))
    );

    this.formFilter.valueChanges.subscribe((values) => {
      this.pageSize = Constants.defaultTableSize;
      this.voucherFilter = {
        ...this.voucherFilter,
        code: values.code,
        startDate: values.date[0],
        endDate: values.date[1],
      };
      this.invokeVoucher();
    });
  }

  getVoucherStatus(voucher: VoucherDto): VoucherStatus[] {
    const now = new Date();
    const expirationDate = new Date(voucher.expirationDate);

    const isExpired = expirationDate < now;
    const isCapacityExceeded = voucher.consumerNumber >= voucher.maxUsers;

    if (isExpired) {
      return [VoucherStatus.EXPIRED];
    }

    if (isCapacityExceeded) {
      return [VoucherStatus.VOUCHER_CAPACITY_EXCEEDED];
    }

    if (!isExpired && !isCapacityExceeded) {
      return [VoucherStatus.VALID];
    }

    return [];
  }

  changePage(event: number) {
    this.pageNumber = this.pageNumber;
    this.voucherFilter = {
      ...this.voucherFilter,
      pager: {
        ...this.voucherFilter.pager,
        pageNumber: event,
      },
    };
    this.invokeVoucher();
  }

  itemsPerPageChange(pageSize: number) {
    this.pageSize = pageSize;
    this.voucherFilter = {
      ...this.voucherFilter,
      pager: {
        ...this.voucherFilter.pager,
        pageSize: this.pageSize,
      },
    };
    this.invokeVoucher();
  }

  invokeVoucher() {
    this.store.dispatch(invokeApiDistroyed());
    this.store.dispatch(
      invokeVouchersForLocationPartner({
        filter: this.voucherFilter,
      })
    );
  }

  saveOptionFilter(options: VoucherStatus[]) {
    let listFounds: any[] = [];
    options?.map((option) => {
      listFounds.push(
        Object.keys(VoucherStatusMapping).find(
          (key) => VoucherStatusMapping[key as VoucherStatus] === option
        )
      );
    });

    this.voucherFilter = {
      ...this.voucherFilter,
      status: listFounds,
    };
    this.invokeVoucher();
  }

  closeModal() {
    this.closeTheModal.emit(true);
  }

  translateVoucherData(vouchers: any[]) {
    const translatedVouchers: any[] = [];
    vouchers.forEach((voucher) => {
      translatedVouchers.push({
        [this.translate.instant("admin.locations.voucher.code")]: voucher.code,
        [this.translate.instant("admin.locations.voucher.creationDate")]:
          voucher.creationDate,
        [this.translate.instant("admin.locations.voucher.expirationDate")]:
          voucher.expirationDate,
        [this.translate.instant("admin.locations.voucher.maxUsers")]:
          voucher.maxUsers,
        [this.translate.instant("admin.locations.voucher.consumerNumber")]:
          voucher.consumerNumber,
        [this.translate.instant("admin.locations.voucher.status")]:
          this.translate.instant(
            "enumMapping.voucher." + this.getVoucherStatus(voucher)
          ),
      });
    });

    return translatedVouchers;
  }
}
